import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import { 
  UserPlus, Search, Mail, Phone, MapPin, Users, DollarSign, 
  Briefcase, ArrowUpDown, Loader2, TrendingUp, ChevronRight,
  Home, Plus, Trash2, Building2, AlertTriangle
} from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import CustomerProfile from './CustomerProfile';

export default function CustomersManagement() {
  const [customers, setCustomers] = useState([]);
  const [customerStats, setCustomerStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [submitting, setSubmitting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Customer form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  
  // Properties data
  const [properties, setProperties] = useState([{
    property_name: 'Home',
    address_line_1: '',
    city: '',
    state: '',
    pincode: '',
    latitude: '',
    longitude: ''
  }]);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const [customersRes, jobsRes, invoicesRes] = await Promise.all([
        axiosInstance.get('/customers'),
        axiosInstance.get('/jobs'),
        axiosInstance.get('/invoices')
      ]);
      
      const customersData = customersRes.data;
      const jobsData = jobsRes.data;
      const invoicesData = invoicesRes.data;
      
      // Calculate stats for each customer
      const stats = {};
      customersData.forEach(customer => {
        const customerJobs = jobsData.filter(j => j.customer_id === customer.id);
        const customerInvoices = invoicesData.filter(i => i.customer_id === customer.id);
        
        stats[customer.id] = {
          totalJobs: customerJobs.length,
          totalRevenue: customerJobs.reduce((sum, j) => sum + (j.quote_amount || 0), 0),
          primaryProperty: customer.address || 'No address'
        };
      });
      
      setCustomers(customersData);
      setCustomerStats(stats);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '' });
    setProperties([{
      property_name: 'Home',
      address_line_1: '',
      city: '',
      state: '',
      pincode: '',
      latitude: '',
      longitude: ''
    }]);
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    
    // Validate at least one property has an address
    const validProperties = properties.filter(p => p.address_line_1.trim() !== '');
    if (validProperties.length === 0) {
      toast.error('Please add at least one property with an address');
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        properties: validProperties.map(p => ({
          property_name: p.property_name || 'Home',
          address_line_1: p.address_line_1,
          city: p.city || null,
          state: p.state || null,
          pincode: p.pincode || null,
          latitude: p.latitude ? parseFloat(p.latitude) : null,
          longitude: p.longitude ? parseFloat(p.longitude) : null
        }))
      };
      
      const response = await axiosInstance.post('/customers', payload);
      toast.success(`Customer created with ${response.data.properties_created} property(s)`);
      setShowAddDialog(false);
      resetForm();
      await fetchCustomers();
      
      // Open the new customer's profile
      if (response.data.customer_id) {
        setSelectedCustomerId(response.data.customer_id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add customer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (e, customer) => {
    e.stopPropagation(); // Prevent row click
    setCustomerToDelete(customer);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!customerToDelete) return;
    
    setDeleting(true);
    try {
      const response = await axiosInstance.delete(`/customers/${customerToDelete.id}`);
      toast.success(response.data.message || 'Customer deleted successfully');
      setDeleteDialogOpen(false);
      setCustomerToDelete(null);
      await fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete customer');
    } finally {
      setDeleting(false);
    }
  };

  const addProperty = () => {
    setProperties([...properties, {
      property_name: 'Home',
      address_line_1: '',
      city: '',
      state: '',
      pincode: '',
      latitude: '',
      longitude: ''
    }]);
  };

  const removeProperty = (index) => {
    if (properties.length > 1) {
      setProperties(properties.filter((_, i) => i !== index));
    }
  };

  const updateProperty = (index, field, value) => {
    const updated = [...properties];
    updated[index][field] = value;
    setProperties(updated);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredCustomers = customers
    .filter(customer =>
      customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.phone?.includes(searchTerm)
    )
    .sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (sortField === 'totalJobs') {
        aVal = customerStats[a.id]?.totalJobs || 0;
        bVal = customerStats[b.id]?.totalJobs || 0;
      } else if (sortField === 'totalRevenue') {
        aVal = customerStats[a.id]?.totalRevenue || 0;
        bVal = customerStats[b.id]?.totalRevenue || 0;
      }
      
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal?.toLowerCase() || '';
      }
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

  // Calculate overview stats
  const totalCustomers = customers.length;
  const totalRevenue = Object.values(customerStats).reduce((sum, s) => sum + (s.totalRevenue || 0), 0);
  const totalJobs = Object.values(customerStats).reduce((sum, s) => sum + (s.totalJobs || 0), 0);
  const avgRevenuePerCustomer = totalCustomers > 0 ? totalRevenue / totalCustomers : 0;

  // If a customer is selected, show their profile
  if (selectedCustomerId) {
    return (
      <CustomerProfile 
        customerId={selectedCustomerId} 
        onBack={() => setSelectedCustomerId(null)} 
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#595959]" />
      </div>
    );
  }

  const propertyNameOptions = ['Home', 'Office', 'Shop', 'Warehouse', 'Factory', 'Site', 'Other'];

  return (
    <div data-testid="customers-management" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Customers
          </h2>
          <p className="text-gray-600">Manage your customer base</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={(open) => {
          setShowAddDialog(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button data-testid="add-customer-btn" className="bg-[#595959] hover:bg-[#404040]">
              <UserPlus size={16} className="mr-2" />
              Add Customer
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>Add New Customer</DialogTitle>
              <DialogDescription>
                Create a new customer account. Customers login using Email OTP only.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddCustomer} className="flex-1 overflow-y-auto space-y-6 pr-2">
              {/* Customer Details Section */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Customer Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      data-testid="customer-name-field"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="customer-email-field"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@example.com"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      id="phone"
                      data-testid="customer-phone-field"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+91 98765 43210"
                      required
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  Customer will login using Email OTP. No password required.
                </p>
              </div>

              {/* Properties Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Home className="w-4 h-4" />
                    Properties
                    <Badge variant="outline" className="ml-2">{properties.length}</Badge>
                  </h3>
                </div>

                {properties.map((property, index) => (
                  <Card key={index} className={`border ${index === 0 ? 'border-teal-300 bg-gray-100/30' : ''}`}>
                    <CardHeader className="py-3 px-4">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-gray-500" />
                          Property {index + 1}
                          {index === 0 && (
                            <Badge className="bg-gray-200 text-[#595959] text-xs">Primary</Badge>
                          )}
                        </CardTitle>
                        {properties.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeProperty(index)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="px-4 pb-4 pt-0 space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-xs">Property Name</Label>
                          <Select
                            value={property.property_name}
                            onValueChange={(value) => updateProperty(index, 'property_name', value)}
                          >
                            <SelectTrigger data-testid={`property-name-${index}`}>
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent>
                              {propertyNameOptions.map(opt => (
                                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Address Line 1 *</Label>
                          <Input
                            value={property.address_line_1}
                            onChange={(e) => updateProperty(index, 'address_line_1', e.target.value)}
                            placeholder="123 Main Street, Apt 4"
                            data-testid={`property-address-${index}`}
                            required={index === 0}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-xs">City</Label>
                          <Input
                            value={property.city}
                            onChange={(e) => updateProperty(index, 'city', e.target.value)}
                            placeholder="Mumbai"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">State</Label>
                          <Input
                            value={property.state}
                            onChange={(e) => updateProperty(index, 'state', e.target.value)}
                            placeholder="Maharashtra"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Pincode</Label>
                          <Input
                            value={property.pincode}
                            onChange={(e) => updateProperty(index, 'pincode', e.target.value)}
                            placeholder="400001"
                          />
                        </div>
                        <div className="space-y-1.5 col-span-1">
                          <Label className="text-xs text-gray-400">Lat / Lng (optional)</Label>
                          <div className="flex gap-1">
                            <Input
                              value={property.latitude}
                              onChange={(e) => updateProperty(index, 'latitude', e.target.value)}
                              placeholder="19.07"
                              className="text-xs"
                            />
                            <Input
                              value={property.longitude}
                              onChange={(e) => updateProperty(index, 'longitude', e.target.value)}
                              placeholder="72.87"
                              className="text-xs"
                            />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                <Button
                  type="button"
                  variant="outline"
                  onClick={addProperty}
                  className="w-full border-dashed"
                  data-testid="add-another-property-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Another Property
                </Button>
              </div>

              {/* Submit Button */}
              <div className="flex-shrink-0 pt-4 border-t">
                <Button 
                  type="submit" 
                  className="w-full bg-[#595959] hover:bg-[#404040]"
                  disabled={submitting}
                  data-testid="submit-add-customer"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating Customer...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add Customer
                    </>
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-teal-600">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-4 h-4 text-[#595959]" />
              <span className="text-xs text-gray-500">Total Customers</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{totalCustomers}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="w-4 h-4 text-green-500" />
              <span className="text-xs text-gray-500">Total Revenue</span>
            </div>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(totalRevenue)}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Briefcase className="w-4 h-4 text-blue-500" />
              <span className="text-xs text-gray-500">Total Jobs</span>
            </div>
            <p className="text-2xl font-bold text-blue-600">{totalJobs}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-purple-500" />
              <span className="text-xs text-gray-500">Avg Revenue/Customer</span>
            </div>
            <p className="text-2xl font-bold text-purple-600">{formatCurrency(avgRevenuePerCustomer)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Customer List */}
      <div className="bg-white rounded-lg border shadow-sm">
        {/* Search and Filters */}
        <div className="p-4 border-b">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <Input
                placeholder="Search by name, email or phone..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="customer-search-input"
              />
            </div>
            <span className="text-sm text-gray-500">{filteredCustomers.length} customers</span>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-gray-50">
                <th 
                  className="text-left p-4 font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center gap-1">
                    Name
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>
                <th className="text-left p-4 font-medium text-gray-600">Phone</th>
                <th className="text-left p-4 font-medium text-gray-600">Email</th>
                <th className="text-left p-4 font-medium text-gray-600">Primary Property</th>
                <th 
                  className="text-center p-4 font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('totalJobs')}
                >
                  <div className="flex items-center justify-center gap-1">
                    Total Jobs
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>
                <th 
                  className="text-right p-4 font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('totalRevenue')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Total Revenue
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>
                <th className="text-center p-4 font-medium text-gray-600 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No customers found</p>
                  </td>
                </tr>
              ) : (
                filteredCustomers.map((customer) => {
                  const stats = customerStats[customer.id] || {};
                  return (
                    <tr 
                      key={customer.id} 
                      className="border-b hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => setSelectedCustomerId(customer.id)}
                      data-testid={`customer-row-${customer.id}`}
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                            <span className="text-[#595959] font-medium">
                              {customer.name?.charAt(0)?.toUpperCase()}
                            </span>
                          </div>
                          <span className="font-medium text-gray-900">{customer.name}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-1 text-gray-600">
                          <Phone className="w-4 h-4 text-gray-400" />
                          {customer.phone || 'N/A'}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-1 text-gray-600">
                          <Mail className="w-4 h-4 text-gray-400" />
                          {customer.email}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-1 text-gray-600 max-w-xs truncate">
                          <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="truncate">{stats.primaryProperty || 'No address'}</span>
                        </div>
                      </td>
                      <td className="p-4 text-center">
                        <Badge variant="outline" className="font-medium">
                          {stats.totalJobs || 0}
                        </Badge>
                      </td>
                      <td className="p-4 text-right">
                        <span className="font-semibold text-green-600">
                          {formatCurrency(stats.totalRevenue)}
                        </span>
                      </td>
                      <td className="p-4 text-center">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                            onClick={(e) => handleDeleteClick(e, customer)}
                            data-testid={`delete-customer-${customer.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete Customer
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                Are you sure you want to delete <strong>{customerToDelete?.name}</strong>?
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                <p className="font-medium mb-1">This will permanently delete:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Customer profile and login access</li>
                  <li>All properties</li>
                  <li>All jobs and quotes</li>
                  <li>All invoices and payment records</li>
                  <li>All notes and documents</li>
                </ul>
              </div>
              <p className="text-red-600 font-medium">This action cannot be undone.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-customer"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Customer
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
